import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Activity, AlertTriangle, RefreshCw, ServerCrash, WalletCards } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import useAdminAutoRefresh from "../../lib/useAdminAutoRefresh";

const SYNC_INTERVAL_MS = 60000;

export default function AdminGameCredits() {
  const [rows, setRows] = useState([]);
  const rowsRef = useRef([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState("");
  const [showSetupNeeded, setShowSetupNeeded] = useState(false);

  const saveRows = useCallback((nextRows) => {
    rowsRef.current = nextRows;
    setRows(nextRows);
  }, []);

  const loadCredits = useCallback(async ({ quiet = false } = {}) => {
    if (quiet) setSyncing(true);
    else setLoading(true);
    try {
      const currentRows = rowsRef.current;
      const liveGameNames = quiet
        ? currentRows
            .filter((row) => row.ok !== false && !row.balanceUnavailable)
            .map(gameIdFromAutomationRow)
            .filter(Boolean)
        : [];
      const { data } = liveGameNames.length
        ? await api.post("/admin/automation/game-credits", { gameNames: liveGameNames })
        : await api.get("/admin/automation/game-credits");
      const nextRows = toRows(data);
      if (liveGameNames.length) {
        const byId = new Map(currentRows.map((row) => [gameIdFromAutomationRow(row), row]));
        nextRows.forEach((row) => byId.set(gameIdFromAutomationRow(row), row));
        saveRows(Array.from(byId.values()));
      } else {
        saveRows(nextRows);
      }
      setError("");
      setLastUpdated(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }));
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
      setSyncing(false);
    }
  }, [saveRows]);

  useEffect(() => {
    loadCredits();
  }, [loadCredits]);

  useAdminAutoRefresh(() => loadCredits({ quiet: true }), { intervalMs: SYNC_INTERVAL_MS });

  const visibleRows = useMemo(() => rows.filter((row) => shouldShowGameCreditRow(row)), [rows]);
  const liveRows = useMemo(() => visibleRows.filter((row) => row.ok !== false), [visibleRows]);
  const setupRows = useMemo(() => visibleRows.filter((row) => needsSetupAttention(row)), [visibleRows]);

  const stats = useMemo(() => {
    const live = visibleRows.length;
    const attention = setupRows.length;
    const total = liveRows.reduce((sum, row) => {
      const value = Number(row.backend_balance);
      return Number.isFinite(value) ? sum + value : sum;
    }, 0);
    return { live, attention, total };
  }, [liveRows, setupRows, visibleRows.length]);

  const hiddenCount = rows.length - visibleRows.length;

  return (
    <div className="p-6 md:p-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Game Credits</h1>
          <p className="mt-1 max-w-3xl text-sm text-slate-500">
            Working games stay visible and refresh automatically. Expired provider sessions are handled in Backend Attachments and stay out of this balance board.
          </p>
        </div>
        <div className="rounded-full border border-emerald-700/30 bg-emerald-500/10 px-3 py-2 text-xs font-semibold text-emerald-300">
          <span className="inline-flex items-center gap-2">
            <RefreshCw className={`h-3.5 w-3.5 ${syncing || loading ? "animate-spin" : ""}`} />
            {syncing || loading ? "Syncing now" : `Live sync every ${SYNC_INTERVAL_MS / 1000}s`}
          </span>
        </div>
      </div>

      <div className="mb-6 grid gap-3 md:grid-cols-4">
        <Stat icon={WalletCards} label="Total backend credit" value={formatMoney(stats.total)} />
        <Stat icon={Activity} label="Enabled games" value={stats.live} />
        <Stat icon={ServerCrash} label="Needs attention" value={stats.attention} />
        <Stat icon={RefreshCw} label="Last sync" value={lastUpdated || "--"} />
      </div>

      {error && (
        <div className="mb-6 rounded-xl border border-red-700/30 bg-red-950/40 p-4 text-sm font-semibold text-red-200">
          {error}
        </div>
      )}

      <section className="rounded-xl border border-white/10 bg-[#121214] p-5">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <WalletCards className="h-5 w-5 text-[#2F6FA5]" />
              <h2 className="text-lg font-semibold">Enabled game balances</h2>
            </div>
            <p className="mt-1 text-sm text-slate-500">
              Enabled games stay visible here. Balances refresh when the provider backend is ready.
            </p>
          </div>
        </div>

        {loading && rows.length === 0 ? (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="h-48 animate-pulse rounded-xl border border-white/10 bg-white/[0.03]" />
            ))}
          </div>
        ) : visibleRows.length > 0 ? (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {visibleRows.map((row) => (
              <GameCreditCard key={gameIdFromAutomationRow(row)} row={row} />
            ))}
          </div>
        ) : (
          <div className="rounded-lg border border-white/5 bg-black/20 p-8 text-center text-sm text-slate-500">
            Attach provider backends first, then their balances will show here.
          </div>
        )}

        {setupRows.length > 0 && (
          <div className="mt-4 rounded-lg border border-amber-500/20 bg-amber-500/10 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <AlertTriangle className="h-5 w-5 shrink-0 text-amber-300" />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-amber-100">
                    {setupRows.length} provider login{setupRows.length === 1 ? "" : "s"} need attention
                  </div>
                  <div className="text-xs text-amber-100/60">
                    Session-based provider logins stay paused until a fresh Provider token/Cookie is saved.
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowSetupNeeded((value) => !value)}
                  className="rounded-md border border-amber-400/30 px-3 py-2 text-xs font-semibold text-amber-100 hover:bg-amber-400/10"
                >
                  {showSetupNeeded ? "Hide" : "Show"}
                </button>
                <a
                  href="/admin/automation"
                  className="rounded-md bg-amber-300 px-3 py-2 text-xs font-bold text-black hover:bg-amber-200"
                >
                  Backend Attachments
                </a>
              </div>
            </div>
            {showSetupNeeded && (
              <div className="mt-4 grid gap-2 md:grid-cols-2">
                {setupRows.map((row) => (
                  <SetupNeededRow key={gameIdFromAutomationRow(row)} row={row} />
                ))}
              </div>
            )}
          </div>
        )}

        {hiddenCount > 0 && (
          <div className="mt-4 rounded-lg border border-white/5 bg-black/20 px-4 py-3 text-xs text-slate-500">
            {hiddenCount} disabled or unavailable game{hiddenCount === 1 ? "" : "s"} hidden from this enabled-games board.
          </div>
        )}
      </section>
    </div>
  );
}

function GameCreditCard({ row }) {
  const gameId = gameIdFromAutomationRow(row);
  const directLiveDisabled = Boolean(row.directLiveDisabled || row.suppressUpstream);
  const providerRetrying = Boolean(row.providerRetrying || row.retrying || row.transientProviderError);
  const setupRequired = needsSetupAttention(row);
  const ok = row.ok !== false || providerRetrying;
  const hasBalance = row.backend_balance !== null && row.backend_balance !== undefined && row.backend_balance !== "";
  const hasRetryError = Boolean(row.retryError);
  const softRetrying = providerRetrying && !hasBalance;
  const balanceUnavailable = ok && !hasBalance && row.balanceUnavailable;
  const status = directLiveDisabled
    ? (hasBalance ? "Group automation balance" : "Group automation active")
    : softRetrying
    ? "Live sync refreshing"
    : ok
      ? (hasBalance ? (hasRetryError ? "Last good balance" : "Backend balance") : balanceUnavailable ? "Balance not exposed" : "No balance returned")
      : "Needs backend setup";
  const detail = directLiveDisabled
    ? row.balanceNote || "This game uses the group automation backend; direct live balance is disabled."
    : softRetrying
    ? row.balanceNote || "Provider is attached. Live balance is refreshing automatically."
    : ok
      ? (hasRetryError ? shortError(row.retryError) : balanceUnavailable ? row.balanceNote || "Provider is connected, but this backend does not expose an agent balance." : "Updates automatically while live.")
      : shortError(row.error);
  const badgeText = directLiveDisabled ? "group" : setupRequired ? "setup" : (softRetrying || hasRetryError) ? "syncing" : "live";

  return (
    <article className={`min-w-0 rounded-xl border p-5 transition-colors ${setupRequired ? "border-amber-500/25 bg-amber-500/10" : directLiveDisabled ? "border-sky-400/20 bg-sky-500/5" : "border-white/10 bg-black/20 hover:border-[#2F6FA5]/30"}`}>
      <div className="mb-4 flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate font-semibold text-slate-100">{row.displayName || row.name || gameId}</h3>
          <p className="mt-1 truncate text-xs uppercase tracking-wider text-slate-500">{gameId}</p>
        </div>
        <span className={`shrink-0 rounded-full border px-2 py-0.5 text-xs ${directLiveDisabled ? "border-sky-400/30 text-sky-200" : setupRequired ? "border-amber-400/30 text-amber-200" : softRetrying ? "border-sky-400/30 text-sky-200" : "border-emerald-700/30 text-emerald-300"}`}>
          {badgeText}
        </span>
      </div>
      <div className="text-3xl font-bold tracking-tight text-white tabular-nums">{formatMoney(row.backend_balance)}</div>
      <div className="mt-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
        {status}
      </div>
      <div className={`mt-4 rounded-lg border px-3 py-2 text-xs ${directLiveDisabled ? "border-sky-400/15 bg-sky-500/5 text-sky-100/75" : setupRequired ? "border-amber-400/15 bg-black/20 text-amber-100/75" : softRetrying ? "border-sky-400/15 bg-sky-500/5 text-sky-100/75" : "border-white/5 bg-white/[0.03] text-slate-500"}`}>
        {detail}
      </div>
      <a
        href={`/admin/automation?gameId=${encodeURIComponent(gameId)}`}
        className={`mt-3 inline-flex w-full items-center justify-center rounded-md border px-3 py-2 text-sm font-semibold ${setupRequired ? "border-amber-400/30 text-amber-100 hover:bg-amber-400/10" : "border-[#2F6FA5]/30 text-[#2F6FA5] hover:bg-[#2F6FA5]/10"}`}
      >
        {setupRequired ? "Fix account" : "Change account"}
      </a>
    </article>
  );
}

function SetupNeededRow({ row }) {
  const gameId = gameIdFromAutomationRow(row);
  return (
    <div className="min-w-0 rounded-md border border-amber-400/15 bg-black/20 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-white">{row.displayName || row.name || gameId}</div>
          <div className="truncate text-[11px] uppercase tracking-wider text-amber-100/50">{gameId}</div>
        </div>
        <span className="shrink-0 rounded-full border border-amber-400/20 px-2 py-0.5 text-[11px] text-amber-200">
          paused
        </span>
      </div>
      <p className="mt-2 line-clamp-2 text-xs text-amber-100/70">{shortError(row.error)}</p>
      <a
        href={`/admin/automation?gameId=${encodeURIComponent(gameId)}`}
        className="mt-3 inline-flex rounded-md border border-amber-400/25 px-3 py-2 text-xs font-semibold text-amber-100 hover:bg-amber-400/10"
      >
        Fix login
      </a>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-white/10 bg-[#121214] p-4">
      <Icon className="mb-3 h-5 w-5 text-[#2F6FA5]" />
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function toRows(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.items)) return value.items;
  if (value && typeof value === "object") return [value];
  return [];
}

function gameIdFromAutomationRow(row = {}) {
  return String(row.gameId || row.gameName || row.id || row.name || "").trim();
}

function shouldShowGameCreditRow(row = {}) {
  if (row.enabledGame) {
    return Boolean(gameIdFromAutomationRow(row));
  }
  if (row.attached === false || row.isAttached === false || row.hasAttachment === false) {
    return false;
  }
  if (row.adapterReady === false && !row.providerRetrying) return false;
  const error = shortError(row.error).toLowerCase();
  if (!row.providerRetrying && isProviderSessionSetupError(error)) return false;
  if (!row.ok && (error.includes("unsupported game") || error.includes("not attached") || error.includes("backend is not attached"))) {
    return false;
  }
  return Boolean(gameIdFromAutomationRow(row));
}

function needsSetupAttention(row = {}) {
  if (row.providerRetrying || row.retrying || row.transientProviderError) return false;
  if (row.setupRequired || row.needsSetup) return true;
  if (row.ok !== false) return false;
  if (row.attached === false || row.isAttached === false || row.hasAttachment === false) return false;
  return true;
}

function isProviderSessionSetupError(error = "") {
  return [
    "session expired",
    "cookie is missing",
    "needs captcha",
    "provider captcha",
    "captcha expired",
    "live provider session",
    "provider session needs refresh",
    "provider token/cookie",
    "provider login or automation setup needs attention",
    "logged-in provider cookie",
    "verification code",
    "attach again",
    "re-attach",
  ].some((term) => error.includes(term));
}

function formatMoney(value) {
  if (value === null || value === undefined || value === "") return "--";
  const number = Number(value);
  if (!Number.isFinite(number)) return "--";
  return `$${number.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function shortError(value) {
  if (!value) return "Provider did not return a balance.";
  if (typeof value === "string") return value;
  if (typeof value?.detail === "string") return value.detail;
  if (typeof value?.error === "string") return value.error;
  if (typeof value?.message === "string") {
    const providerMessage = value?.response?.message || value?.response?.error;
    return providerMessage ? `${value.message}: ${providerMessage}` : value.message;
  }
  try {
    return JSON.stringify(value);
  } catch {
    return "Provider error";
  }
}
